import React from 'react';
import { Button, message, Modal } from 'antd';
import { useState, useRef, useEffect } from 'react';
import { ImageTheme } from '../../../components/images';
import exBarcode from '../../../assets/images/ex_take_barcode_photo.jpg';
import { color } from '../../../resources';
import { TextSmall } from '../../../components/text';
import { cardStyle } from '../styles/styles';
import { UPLOAD, POST_SCAN_BARCODE_IMAGE } from '../../../services';

// ฟังก์ชันสำหรับบีบอัดรูปภาพ
const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // คำนวณขนาดใหม่โดยรักษาอัตราส่วน
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;

      canvas.width = newWidth;
      canvas.height = newHeight;

      // วาดรูปภาพลงใน canvas พร้อมขนาดใหม่
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // แปลงกลับเป็น blob
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };

    // อ่านไฟล์เป็น data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// ฟังก์ชันสำหรับจัดการ EXIF orientation
const getOrientation = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const view = new DataView(e.target.result);
      if (view.getUint16(0, false) !== 0xffd8) {
        resolve(-2); // ไม่ใช่ไฟล์ JPEG
      }
      const length = view.byteLength;
      let offset = 2;
      while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8) {
          resolve(-1);
        }
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xffe1) {
          if (view.getUint32((offset += 2), false) !== 0x45786966) {
            resolve(-1);
          }
          const little = view.getUint16((offset += 6), false) === 0x4949;
          offset += view.getUint32(offset + 4, little);
          const tags = view.getUint16(offset, little);
          offset += 2;
          for (let i = 0; i < tags; i++) {
            if (view.getUint16(offset + i * 12, little) === 0x0112) {
              resolve(view.getUint16(offset + i * 12 + 8, little));
            }
          }
        } else if ((marker & 0xff00) !== 0xff00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }
      resolve(-1);
    };
    reader.readAsArrayBuffer(file);
  });
};

// ฟังก์ชันสำหรับหมุนรูปภาพตาม EXIF orientation
const rotateImage = (file, orientation) => {
  return new Promise((resolve) => {
    if (orientation < 2) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;

      // ตั้งค่าขนาด canvas ตาม orientation
      if (orientation > 4) {
        canvas.width = height;
        canvas.height = width;
      } else {
        canvas.width = width;
        canvas.height = height;
      }

      // หมุนรูปภาพตาม orientation
      switch (orientation) {
        case 2:
          ctx.transform(-1, 0, 0, 1, width, 0);
          break;
        case 3:
          ctx.transform(-1, 0, 0, -1, width, height);
          break;
        case 4:
          ctx.transform(1, 0, 0, -1, 0, height);
          break;
        case 5:
          ctx.transform(0, 1, 1, 0, 0, 0);
          break;
        case 6:
          ctx.transform(0, 1, -1, 0, height, 0);
          break;
        case 7:
          ctx.transform(0, -1, -1, 0, height, width);
          break;
        case 8:
          ctx.transform(0, -1, 1, 0, 0, width);
          break;
      }

      ctx.drawImage(img, 0, 0);
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// Camera Preview Component พร้อมกรอบแนะนำ
const CameraPreview = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' หรือ 'environment'

  const startCamera = async (facing = 'environment') => {
    try {
      setIsLoading(true);
      setError(null);

      // หยุด stream เก่าก่อน
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsLoading(false);
    } catch (err) {
      message.error(err);
      setError('ไม่สามารถเข้าถึงกล้องได้ กรุณาอนุญาตการใช้งานกล้อง');
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const switchCamera = () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    startCamera(newFacing);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        const file = new File([blob], `barcode_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        stopCamera();
      },
      'image/jpeg',
      0.9,
    );
  };

  useEffect(() => {
    startCamera(facingMode);
    return stopCamera;
  }, []);

  const cameraContainerStyle = {
    position: 'relative',
    width: '100%',
    height: '400px',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const videoStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  };

  const frameStyle = {
    width: '280px',
    height: '180px',
    border: '3px solid #1890ff',
    borderRadius: '12px',
    backgroundColor: 'rgba(24, 144, 255, 0.1)',
    position: 'relative',
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
  };

  const cornerStyle = {
    position: 'absolute',
    width: '20px',
    height: '20px',
    border: '3px solid #fff',
  };

  const instructionStyle = {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    textAlign: 'center',
    maxWidth: '300px',
  };

  const controlsStyle = {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    pointerEvents: 'auto',
  };

  const captureButtonStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#1890ff',
    border: '4px solid #fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#fff',
  };

  const switchButtonStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: '#333',
  };

  if (error) {
    return (
      <div style={cameraContainerStyle}>
        <div style={{ color: '#fff', textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '16px', marginBottom: '10px' }}>❌ {error}</div>
          <Button onClick={() => startCamera(facingMode)} style={{ marginRight: '10px' }}>
            ลองใหม่
          </Button>
          <Button onClick={onClose}>ปิด</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={cameraContainerStyle}>
      {isLoading && <div style={{ color: '#fff', fontSize: '16px' }}>📱 กำลังเปิดกล้อง...</div>}

      <video ref={videoRef} style={videoStyle} playsInline muted />

      {!isLoading && (
        <>
          {/* กรอบแนะนำตำแหน่งบาร์โค้ด */}
          <div style={overlayStyle}>
            <div style={frameStyle}>
              {/* มุมกรอบ */}
              <div
                style={{
                  ...cornerStyle,
                  top: '-3px',
                  left: '-3px',
                  borderRight: 'none',
                  borderBottom: 'none',
                }}
              />
              <div
                style={{
                  ...cornerStyle,
                  top: '-3px',
                  right: '-3px',
                  borderLeft: 'none',
                  borderBottom: 'none',
                }}
              />
              <div
                style={{
                  ...cornerStyle,
                  bottom: '-3px',
                  left: '-3px',
                  borderRight: 'none',
                  borderTop: 'none',
                }}
              />
              <div
                style={{
                  ...cornerStyle,
                  bottom: '-3px',
                  right: '-3px',
                  borderLeft: 'none',
                  borderTop: 'none',
                }}
              />
            </div>
          </div>

          {/* คำแนะนำ */}
          <div style={instructionStyle}>
            📋 วางบาร์โค้ดในกรอบสีน้ำเงิน
            <br />
            💡 ให้แสงเพียงพอและถือให้นิ่ง
          </div>

          {/* ปุ่มควบคุม */}
          <div style={controlsStyle}>
            <button style={switchButtonStyle} onClick={switchCamera} title="สลับกล้อง">
              🔄
            </button>
            <button style={captureButtonStyle} onClick={capturePhoto} title="ถ่ายรูป">
              📷
            </button>
            <button
              style={switchButtonStyle}
              onClick={() => {
                stopCamera();
                onClose();
              }}
              title="ปิด"
            >
              ✕
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export const BarcodeScannerModal = ({ open, onClose, onDetected }) => {
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleOpenCamera = () => {
    setShowOptions(false);
    setShowCamera(true);
  };

  const handleOpenFilePicker = () => {
    setShowOptions(false);
    fileInputRef.current?.click();
  };

  const handleCameraCapture = (file) => {
    setShowCamera(false);
    handleImageUpload({ target: { files: [file] } }, true);
  };

  const handleCloseCameraPreview = () => {
    setShowCamera(false);
    setShowOptions(true);
  };

  const handleImageUpload = async (event, isCamera = false) => {
    const file = event.target.files?.[0] || event.target.files;
    if (!file) return;

    setLoading(true);
    let processingMessage = 'กำลังประมวลผลรูปภาพ...';

    try {
      message.loading({ content: processingMessage, key: 'processing' });

      let processedFile = file;

      // จัดการ EXIF orientation สำหรับรูปที่ถ่ายจากกล้อง
      if (isCamera) {
        processingMessage = 'กำลังปรับแต่งรูปภาพ...';
        message.loading({ content: processingMessage, key: 'processing' });

        const orientation = await getOrientation(file);
        if (orientation > 1) {
          processedFile = await rotateImage(file, orientation);
        }
      }

      // บีบอัดรูปภาพ
      processingMessage = 'กำลังบีบอัดรูปภาพ...';
      message.loading({ content: processingMessage, key: 'processing' });

      const compressedFile = await compressImage(processedFile, 1200, 0.8);

      // สร้าง FormData
      processingMessage = 'กำลังสแกนบาร์โค้ด...';
      message.loading({ content: processingMessage, key: 'processing' });

      const formData = new FormData();
      // สร้างไฟล์ใหม่พร้อมชื่อที่เหมาะสม
      const finalFile = new File([compressedFile], `barcode_${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      formData.append('image', finalFile);

      // ส่งไปยัง API
      const response = await UPLOAD(POST_SCAN_BARCODE_IMAGE(), formData);

      message.destroy('processing');

      if (
        response.success &&
        response.result &&
        response.result.success &&
        response.result.barcodeValue
      ) {
        setScanResult(response.result.barcodeValue);
        onDetected(response.result.barcodeValue);
        message.success(
          `สแกนบาร์โค้ดสำเร็จ: ${response.result.barcodeValue} (${response.result.format})`,
        );
        onClose();
      } else {
        const errorMessage =
          response.result?.message || response.message || 'ไม่สามารถอ่านบาร์โค้ดจากภาพนี้ได้';
        message.error(errorMessage);
      }
    } catch (error) {
      message.destroy('processing');
      message.error(
        error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการสแกนบาร์โค้ด',
      );
    } finally {
      setLoading(false);
      // รีเซ็ต input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // รีเซ็ต state เมื่อปิด modal
  const handleModalClose = () => {
    setShowOptions(false);
    setShowCamera(false);
    setScanResult(null);
    onClose();
  };

  return (
    <Modal
      title="สแกนบาร์โค้ด"
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width={showCamera ? 600 : 500}
      style={{ top: showCamera ? 20 : undefined }}
    >
      {showCamera ? (
        <CameraPreview onCapture={handleCameraCapture} onClose={handleCloseCameraPreview} />
      ) : (
        <>
          <div style={cardStyle}>
            <TextSmall
              text={<span>เลือกรูปภาพที่มีบาร์โค้ดเพื่อสแกน</span>}
              style={{ color: '#8c6e19' }}
              bold
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <ImageTheme
              src={exBarcode}
              style={{
                width: '50%',
                maxWidth: 400,
                borderRadius: '5px',
                display: 'block',
                margin: 'auto',
              }}
            />
          </div>

          {/* Input สำหรับการเลือกไฟล์จากอัลบัม */}
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={(e) => handleImageUpload(e, false)}
          />

          {!showOptions ? (
            <Button
              type="primary"
              onClick={() => setShowOptions(true)}
              loading={loading}
              style={{ width: '100%', background: color.blue, marginBottom: 20 }}
              size="large"
            >
              {loading ? 'กำลังประมวลผล...' : 'เลือกหรือถ่ายภาพบาร์โค้ด'}
            </Button>
          ) : (
            <div style={{ marginBottom: 20 }}>
              <Button
                type="primary"
                onClick={handleOpenCamera}
                loading={loading}
                style={{
                  width: '100%',
                  background: color.blue,
                  marginBottom: 8,
                  border: 'none',
                }}
                size="large"
              >
                📷 ถ่ายรูปใหม่ (แนะนำ)
              </Button>

              <Button
                type="default"
                onClick={handleOpenFilePicker}
                loading={loading}
                style={{
                  width: '100%',
                  marginBottom: 8,
                }}
                size="large"
              >
                🖼️ เลือกจากอัลบัม
              </Button>

              <Button
                type="text"
                onClick={() => setShowOptions(false)}
                style={{
                  width: '100%',
                  color: '#999',
                }}
                size="small"
              >
                ยกเลิก
              </Button>
            </div>
          )}

          {scanResult && (
            <p style={{ textAlign: 'center', marginTop: 20 }}>
              บาร์โค้ดที่พบ: <strong>{scanResult}</strong>
            </p>
          )}
        </>
      )}
    </Modal>
  );
};
